import { z } from 'zod';

/**
 * ATS (Applicant Tracking System) API schemas
 * These schemas mirror the FastAPI Pydantic models for type safety
 */

export const EvidenceItemSchema = z.object({
  section: z.string().describe('Section where match was found'),
  snippet: z.string().describe('Text snippet with context'),
  score: z.number().min(0).max(1).describe('Match score for this evidence'),
});

export const KeywordMatchSchema = z.object({
  keyword: z.string().describe('The extracted keyword'),
  status: z.enum(['missing', 'weak', 'found']).describe('Match status'),
  confidence: z.number().min(0).max(1).describe('Confidence score'),
  evidence: z.array(EvidenceItemSchema).default([]).describe('Evidence snippets'),
});

export const MatchMetaSchema = z.object({
  job_keywords_total: z.number().describe('Total keywords extracted from job'),
  resume_tokens: z.number().describe('Approximate token count in resume'),
});

export const MatchRequestSchema = z.object({
  job_title: z.string().optional().describe('Optional job title'),
  job_text: z.string().min(1).describe('Job description text'),
  resume_text: z.string().min(1).describe('Resume text'),
});

export const MatchResponseSchema = z.object({
  match_score: z.number().min(0).max(100).describe('Overall match score 0-100'),
  hard_skills: z.array(KeywordMatchSchema).default([]).describe('Hard skills matches'),
  soft_skills: z.array(KeywordMatchSchema).default([]).describe('Soft skills matches'),
  other: z.array(KeywordMatchSchema).default([]).describe('Other keywords matches'),
  meta: MatchMetaSchema.describe('Metadata about the matching process'),
});

// Type exports
export type EvidenceItem = z.infer<typeof EvidenceItemSchema>;
export type KeywordMatch = z.infer<typeof KeywordMatchSchema>;
export type MatchMeta = z.infer<typeof MatchMetaSchema>;
export type MatchRequest = z.infer<typeof MatchRequestSchema>;
export type MatchResponse = z.infer<typeof MatchResponseSchema>;

export const ExtractedKeywordSchema = z.object({
  keyword: z.string(),
  category: z.enum(['hard', 'soft', 'other']).optional(),
  count: z.number().optional(),
  uiSection: z.enum(['requirements', 'responsibilities', 'preferred', 'about']).optional(),
  semanticType: z.string().optional(),
  confidence: z.number().optional(),
  weight: z.number().optional(),
});

export const ExtractKeywordsRequestSchema = z.object({
  job_text: z.string().min(1),
  company_name: z.string().optional(),
});

export const ExtractKeywordsResponseSchema = z.object({
  keywords: z.array(ExtractedKeywordSchema).default([]),
  requirements: z.array(ExtractedKeywordSchema).default([]),
  responsibilities: z.array(ExtractedKeywordSchema).default([]),
  preferred: z.array(ExtractedKeywordSchema).default([]),
  about: z.array(ExtractedKeywordSchema).default([]),
});

export type ExtractedKeyword = z.infer<typeof ExtractedKeywordSchema>;
export type ExtractKeywordsRequest = z.infer<typeof ExtractKeywordsRequestSchema>;
export type ExtractKeywordsResponse = z.infer<typeof ExtractKeywordsResponseSchema>;
